import React, { useEffect, useState } from 'react';
import { UserPlus, Edit, Trash2, Shield, X } from 'lucide-react';
import { getAllUsers, deleteUser } from '../api/users';
import { getTasks } from '../api/tasks';
import { User } from '../types/user';
import { Task } from '../types/task';
import UserForm from '../components/UserForm';
import UserRoles from '../components/UserRoles';
import DeleteUserModal from '../components/DeleteUserModal';
import Button from '../components/Button';
import Card from '../components/Card';
import EmptyStateCard from '../components/EmptyStateCard';
import { CardSkeleton } from '../components/LoadingSkeleton';
import { showToast } from '../utils/toast';

const UsersPage: React.FC = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [tasks, setTasks] = useState<Task[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [editingUser, setEditingUser] = useState<User | undefined>(undefined);
    const [deletingUser, setDeletingUser] = useState<User | null>(null);
    const [viewingRolesFor, setViewingRolesFor] = useState<string | null>(null);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const [usersData, tasksData] = await Promise.all([
                getAllUsers(),
                getTasks(),
            ]);
            setUsers(usersData);
            setTasks(tasksData);
            setError(null);
        } catch (err) {
            setError('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const handleSuccess = (updatedUser: User) => {
        if (editingUser) {
            setUsers(users.map(u => u.id === updatedUser.id ? updatedUser : u));
            setEditingUser(undefined);
            showToast.success('User updated successfully!');
        } else {
            setUsers([...users, updatedUser]);
            showToast.success('User created successfully!');
        }
        setShowForm(false);
    };

    const handleEdit = (user: User) => {
        setEditingUser(user);
        setShowForm(true);
    };

    const handleDeleteClick = (user: User) => {
        setDeletingUser(user);
    };

    const handleDeleteConfirm = async (cascade: boolean, reassignTo?: string) => {
        if (!deletingUser) return;
        
        try {
            await deleteUser(deletingUser.id, cascade, reassignTo);
            setUsers(users.filter(u => u.id !== deletingUser.id));
            
            // Reload tasks to reflect changes
            const updatedTasks = await getTasks();
            setTasks(updatedTasks);
            
            setDeletingUser(null);
            setError(null);
            showToast.success('User deleted successfully!');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
            setError(errorMessage);
            showToast.error(errorMessage);
            setDeletingUser(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeletingUser(null);
    };

    const handleCancelForm = () => {
        setShowForm(false);
        setEditingUser(undefined);
    };

    if (loading) {
        return (
            <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
                <div className="flex justify-between items-center mb-8">
                    <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                    <div className="h-10 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                    <CardSkeleton showFooter={true} />
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl 2xl:max-w-[1600px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-8">
            <div className="flex flex-wrap justify-between items-center mb-8 gap-4">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent">Users</h1>
                <Button 
                    onClick={() => setShowForm(!showForm)} 
                    variant="primary"
                    leftIcon={showForm ? X : UserPlus}
                >
                    {showForm ? 'Hide Form' : 'Add New User'}
                </Button>
            </div>

            {error && (
                <div className="mb-6 rounded-lg p-4 bg-error/10 dark:bg-error/20 border border-error/30 text-error">
                    {error}
                </div>
            )}

            {showForm && (
                <div className="mb-8">
                    <UserForm 
                        user={editingUser}
                        onSuccess={handleSuccess}
                        onCancel={handleCancelForm}
                    />
                </div>
            )}

            {deletingUser && (
                <DeleteUserModal
                    user={deletingUser}
                    taskCount={tasks.filter(t => t.assigned_to === deletingUser.id).length}
                    availableUsers={users}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />
            )}

            {users.length === 0 ? (
                <EmptyStateCard
                    icon="👤"
                    title="No users found"
                    message="There are no users to display."
                />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {users.map(user => (
                        <Card key={user.id} variant="glass" className="hover:-translate-y-1 transition-all duration-300">
                            <Card.Header>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                        {user.full_name || user.username}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                        @{user.username}
                                    </p>
                                </div>
                                <span className="text-sm text-gray-900 dark:text-gray-100">ID: {user.id}</span>
                            </Card.Header>
                            <Card.Body>
                                <p className="text-gray-900 dark:text-gray-100 mb-2">
                                    <strong>Email:</strong> {user.email}
                                </p>
                                {user.department && (
                                    <p className="text-gray-900 dark:text-gray-100 mb-2">
                                        <strong>Department:</strong> {user.department}
                                    </p>
                                )}
                                {user.skills && (
                                    <p className="text-gray-900 dark:text-gray-100 mb-2">
                                        <strong>Skills:</strong> {user.skills}
                                    </p>
                                )}
                                {user.bio && (
                                    <p className="text-gray-600 dark:text-gray-400 mb-2 text-sm italic">
                                        {user.bio}
                                    </p>
                                )}
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Created: {new Date(user.created_at).toLocaleDateString()}
                                </p>

                                {/* User Roles Section */}
                                {viewingRolesFor === user.id && (
                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <UserRoles userId={user.id} />
                                    </div>
                                )}
                            </Card.Body>
                            <Card.Footer>
                                <Button 
                                    className="w-full sm:w-auto"
                                    onClick={() => handleEdit(user)} 
                                    variant="primary" 
                                    size="sm"
                                    leftIcon={Edit}
                                >
                                    Edit
                                </Button>
                                <Button 
                                    className="w-full sm:w-auto"
                                    onClick={() => setViewingRolesFor(viewingRolesFor === user.id ? null : user.id)}
                                    variant={viewingRolesFor === user.id ? "accent" : "outline"}
                                    size="sm"
                                    leftIcon={Shield}
                                >
                                    {viewingRolesFor === user.id ? 'Hide Roles' : 'View Roles'}
                                </Button>
                                <Button 
                                    className="w-full sm:w-auto"
                                    onClick={() => handleDeleteClick(user)} 
                                    variant="danger" 
                                    size="sm"
                                    leftIcon={Trash2}
                                >
                                    Delete
                                </Button>
                            </Card.Footer>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UsersPage;